/**
 * Telefone celular brasileiro: máscara, validação e derivação dos dois formatos que o sistema usa.
 *
 * ## Por que um módulo só para isso
 *
 * O mesmo número aparece de duas formas no documento que vai ao cliente:
 *
 * | Onde | Formato | Exemplo |
 * | --- | --- | --- |
 * | Link do WhatsApp (`wa.me`) | E.164 sem o `+`, só dígitos | `5531991259696` |
 * | Exibição na proposta | legível, no padrão da marca | `+55 31 99125-9696` |
 *
 * Manter os dois como campos independentes de digitação foi um erro do modelo antigo: são o mesmo
 * número, então guardar duas fontes convida à divergência. Já aconteceu — dois registros do
 * `sellers.json` legado tinham WhatsApp sem o nono dígito, e o botão do cliente abria conversa com
 * número inexistente.
 *
 * Aqui a pessoa digita uma vez e as duas formas são **derivadas**. Divergir passa a ser impossível.
 *
 * ## Por que exigir celular, e não aceitar fixo
 *
 * A proposta tem botão de WhatsApp. WhatsApp não funciona em linha fixa. Aceitar um fixo produziria
 * um botão que falha silenciosamente na mão do cliente — pior que não ter botão, porque o cliente
 * conclui que a empresa deu número errado.
 *
 * O celular brasileiro tem nono dígito desde 2016: DDD de dois dígitos, depois `9`, depois oito
 * dígitos. Total de 11 dígitos nacionais, 13 com o código do país.
 *
 * ## Por que validar o DDD contra uma lista
 *
 * `/^55\d{2}9\d{8}$/` aceita DDD `00`, `01`, `99`… A Anatel não usa todos. Um DDD inválido gera
 * link que o WhatsApp rejeita, e o erro só aparece quando o cliente clica — longe de quem digitou.
 * Barrar na entrada é a diferença entre um aviso no cadastro e uma venda perdida.
 */

/**
 * DDDs em uso no Brasil, conforme o Plano Geral de Códigos Nacionais da Anatel.
 *
 * A lista tem buracos de propósito: `20`, `23`, `25`, `26`, `29`, `30`, `39`, `40`, `50`, `52`,
 * `56` a `60`, `70`, `72`, `76`, `78`, `80` e `90` não são atribuídos. Não são esquecimento.
 */
const DDDS_VALIDOS: ReadonlySet<string> = new Set([
  // Sudeste
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19', // São Paulo
  '21',
  '22',
  '24', // Rio de Janeiro
  '27',
  '28', // Espírito Santo
  '31',
  '32',
  '33',
  '34',
  '35',
  '37',
  '38', // Minas Gerais
  // Sul
  '41',
  '42',
  '43',
  '44',
  '45',
  '46', // Paraná
  '47',
  '48',
  '49', // Santa Catarina
  '51',
  '53',
  '54',
  '55', // Rio Grande do Sul
  // Centro-Oeste
  '61', // Distrito Federal e Goiás
  '62',
  '64', // Goiás
  '63', // Tocantins
  '65',
  '66', // Mato Grosso
  '67', // Mato Grosso do Sul
  // Nordeste
  '68', // Acre
  '69', // Rondônia
  '71',
  '73',
  '74',
  '75',
  '77', // Bahia
  '79', // Sergipe
  '81',
  '87', // Pernambuco
  '82', // Alagoas
  '83', // Paraíba
  '84', // Rio Grande do Norte
  '85',
  '88', // Ceará
  '86',
  '89', // Piauí
  // Norte
  '91',
  '93',
  '94', // Pará
  '92',
  '97', // Amazonas
  '95', // Roraima
  '96', // Amapá
  '98',
  '99', // Maranhão
]);

/** Código do país, fixo. O sistema atende só a operação brasileira. */
export const CODIGO_PAIS_BR = '55';

/** Resultado da normalização: os dois formatos derivados, ou a razão da recusa. */
export type TelefoneNormalizado =
  | {
      readonly valido: true;
      /** Só dígitos, com o código do país, para o `wa.me`. Treze dígitos. */
      readonly e164: string;
      /** Legível, no padrão da marca, para impressão na proposta. */
      readonly exibicao: string;
      /** DDD isolado, útil para relatório e conferência. */
      readonly ddd: string;
    }
  | {
      readonly valido: false;
      /** Mensagem pronta para a tela, dizendo o que corrigir. */
      readonly motivo: string;
    };

/** Descarta tudo que não é dígito. */
function somenteDigitos(entrada: string): string {
  return entrada.replace(/\D/g, '');
}

/**
 * Reduz a entrada aos onze dígitos nacionais.
 *
 * Aceita o que a pessoa provavelmente vai digitar ou colar: com ou sem `+55`, com ou sem zero de
 * operadora, com parêntese, espaço, hífen ou ponto. Ser tolerante aqui evita a recusa que faz a
 * pessoa desistir do cadastro — a rigidez fica na validação, não na leitura.
 */
function extrairNacional(digitos: string): string {
  let d = digitos;

  // `+55` colado ou digitado. Só remove quando o que sobra tem tamanho de número nacional, para não
  // mutilar um DDD 55 (Rio Grande do Sul) de quem digitou apenas os onze dígitos.
  if (d.startsWith(CODIGO_PAIS_BR) && d.length > 11) {
    d = d.slice(2);
  }

  // Zero de operadora, hábito de quem discava interurbano.
  if (d.startsWith('0') && d.length > 11) {
    d = d.slice(1);
  }

  return d;
}

/**
 * Valida e deriva os dois formatos.
 *
 * As mensagens de recusa dizem o que corrigir, não apenas que está errado: quem cadastra o próprio
 * telefone erra por descuido, e "faltam dígitos" é inútil comparado a "informe o nono dígito".
 */
export function normalizarTelefoneBr(entrada: string): TelefoneNormalizado {
  const nacional = extrairNacional(somenteDigitos(entrada));

  if (nacional === '') {
    return { valido: false, motivo: 'Informe o telefone celular com DDD.' };
  }

  if (nacional.length < 11) {
    return {
      valido: false,
      motivo:
        nacional.length === 10
          ? 'Faltou o nono dígito. Celular tem 9 dígitos depois do DDD, começando em 9.'
          : `Faltam dígitos: informe DDD e o número completo, 11 dígitos no total. Você digitou ${String(nacional.length)}.`,
    };
  }

  if (nacional.length > 11) {
    return {
      valido: false,
      motivo: `Dígitos demais: o celular tem 11 no total, com DDD. Você digitou ${String(nacional.length)}.`,
    };
  }

  const ddd = nacional.slice(0, 2);
  if (!DDDS_VALIDOS.has(ddd)) {
    return {
      valido: false,
      motivo: `DDD ${ddd} não existe no Brasil. Confira os dois primeiros dígitos.`,
    };
  }

  const assinante = nacional.slice(2);
  if (!assinante.startsWith('9')) {
    return {
      valido: false,
      motivo:
        'O número precisa ser celular, porque a proposta traz botão de WhatsApp, que não funciona em telefone fixo. Celular começa em 9 depois do DDD.',
    };
  }

  return {
    valido: true,
    e164: `${CODIGO_PAIS_BR}${nacional}`,
    exibicao: `+${CODIGO_PAIS_BR} ${ddd} ${assinante.slice(0, 5)}-${assinante.slice(5)}`,
    ddd,
  };
}

/**
 * Máscara progressiva para o campo de digitação.
 *
 * Formata o que já foi digitado sem exigir que esteja completo, porque a máscara roda a cada tecla.
 * O `+55` **não** entra no valor: fica como prefixo fixo ao lado do campo, o que impede a pessoa de
 * apagá-lo por acidente e deixa claro que não é editável.
 *
 * ```
 * ''          → ''
 * '3'         → '3'
 * '31'        → '31'
 * '319'       → '31 9'
 * '3199125'   → '31 99125'
 * '31991259696' → '31 99125-9696'
 * ```
 *
 * Dígitos além do décimo primeiro são descartados em silêncio: avisar a cada tecla excedente
 * atrapalha quem está colando um número com formatação.
 */
export function aplicarMascaraTelefoneBr(entrada: string): string {
  const d = extrairNacional(somenteDigitos(entrada)).slice(0, 11);

  if (d.length <= 2) return d;
  if (d.length <= 7) return `${d.slice(0, 2)} ${d.slice(2)}`;
  return `${d.slice(0, 2)} ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Converte o formato de exibição de volta para o conteúdo do campo mascarado.
 *
 * Necessário ao abrir um cadastro existente: o banco guarda `+55 31 99125-9696`, e o campo mostra
 * `31 99125-9696`, sem o prefixo que é fixo na tela.
 */
export function exibicaoParaCampo(exibicao: string): string {
  return aplicarMascaraTelefoneBr(exibicao);
}
